import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sparkles,
  Download,
  Cpu,
  Shield,
  Globe,
  Zap,
  Headphones,
  Layers,
  ArrowRight,
  Gift,
} from "lucide-react";
import { PublicNavbar, PublicFooter } from "./PublicLayout";
import "./LandingPage.css";

function LandingPage() {
  const { t } = useTranslation();

  const brands = [
    "Brother", "Janome", "Husqvarna", "Bernina", "Pfaff",
    "Tajima", "Barudan", "Singer", "Babylock", "Melco",
    "Elna", "ZSK",
  ];

  const steps = [
    {
      icon: Sparkles,
      title: t("landing.step1Title"),
      desc: t("landing.step1Desc"),
      gradient: "linear-gradient(135deg, #8b5cf6, #a78bfa)",
    },
    {
      icon: Cpu,
      title: t("landing.step2Title"),
      desc: t("landing.step2Desc"),
      gradient: "linear-gradient(135deg, #667eea, #818cf8)",
    },
    {
      icon: Download,
      title: t("landing.step3Title"),
      desc: t("landing.step3Desc"),
      gradient: "linear-gradient(135deg, #10b981, #34d399)",
    },
  ];

  const features = [
    {
      icon: Sparkles,
      title: t("landing.feat1Title"),
      desc: t("landing.feat1Desc"),
      bg: "rgba(139, 92, 246, 0.08)",
      color: "#8b5cf6",
    },
    {
      icon: Layers,
      title: t("landing.feat2Title"),
      desc: t("landing.feat2Desc"),
      bg: "rgba(59, 130, 246, 0.08)",
      color: "#3b82f6",
    },
    {
      icon: Zap,
      title: t("landing.feat3Title"),
      desc: t("landing.feat3Desc"),
      bg: "rgba(245, 158, 11, 0.08)",
      color: "#f59e0b",
    },
    {
      icon: Shield,
      title: t("landing.feat4Title"),
      desc: t("landing.feat4Desc"),
      bg: "rgba(16, 185, 129, 0.08)",
      color: "#10b981",
    },
    {
      icon: Globe,
      title: t("landing.feat5Title"),
      desc: t("landing.feat5Desc"),
      bg: "rgba(102, 126, 234, 0.08)",
      color: "#667eea",
    },
    {
      icon: Headphones,
      title: t("landing.feat6Title"),
      desc: t("landing.feat6Desc"),
      bg: "rgba(239, 68, 68, 0.08)",
      color: "#ef4444",
    },
  ];

  return (
    <div className="landing-page">
      <PublicNavbar />

      {/* ===== HERO ===== */}
      <section className="lp-hero">
        <div className="lp-hero-bg">
          <div className="lp-hero-shape lp-hero-shape-1" />
          <div className="lp-hero-shape lp-hero-shape-2" />
          <div className="lp-hero-shape lp-hero-shape-3" />
          <div className="lp-hero-pattern" />
        </div>
        <div className="lp-hero-content">
          <div className="lp-hero-badge">
            <span className="lp-hero-badge-dot" />
            {t("landing.heroBadge")}
          </div>
          <h1>
            {t("landing.heroTitle1")}{" "}
            <span className="lp-highlight">{t("landing.heroHighlight")}</span>{" "}
            {t("landing.heroTitle2")}
          </h1>
          <p className="lp-hero-subtitle">{t("landing.heroSubtitle")}</p>
          <div className="lp-hero-actions">
            <Link to="/signup" className="lp-btn lp-btn-white">
              {t("landing.heroCtaPrimary")}
              <ArrowRight size={18} />
            </Link>
            <a href="#how-it-works" className="lp-btn lp-btn-ghost">
              {t("landing.heroCtaSecondary")}
            </a>
          </div>
          <div className="lp-hero-stats">
            <div className="lp-hero-stat">
              <div className="lp-hero-stat-value">21+</div>
              <div className="lp-hero-stat-label">{t("landing.statFormats")}</div>
            </div>
            <div className="lp-hero-stat">
              <div className="lp-hero-stat-value">17</div>
              <div className="lp-hero-stat-label">{t("landing.statBrands")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BRANDS BAR ===== */}
      <section className="lp-brands">
        <div className="lp-brands-label">{t("landing.brandsLabel")}</div>
        <div className="lp-brands-grid">
          {brands.map((brand) => (
            <span key={brand} className="lp-brand-item">{brand}</span>
          ))}
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="lp-section">
        <div className="lp-section-header">
          <div className="lp-section-tag">{t("landing.howTag")}</div>
          <h2 className="lp-section-title">{t("landing.howTitle")}</h2>
          <p className="lp-section-desc">{t("landing.howDesc")}</p>
        </div>
        <div className="lp-steps">
          {steps.map((step, i) => (
            <div className="lp-step" key={i}>
              <div className="lp-step-icon" style={{ background: step.gradient }}>
                <step.icon size={24} />
              </div>
              <div className="lp-step-content">
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="lp-section lp-section-alt">
        <div className="lp-section-header">
          <div className="lp-section-tag">{t("landing.featTag")}</div>
          <h2 className="lp-section-title">{t("landing.featTitle")}</h2>
          <p className="lp-section-desc">{t("landing.featDesc")}</p>
        </div>
        <div className="lp-features">
          {features.map((feat, i) => (
            <div className="lp-feature" key={i}>
              <div className="lp-feature-icon" style={{ background: feat.bg, color: feat.color }}>
                <feat.icon size={22} />
              </div>
              <h3>{feat.title}</h3>
              <p>{feat.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="lp-cta">
        <div className="lp-cta-bg" />
        <div className="lp-cta-content">
          <h2>{t("landing.ctaTitle")}</h2>
          <p>{t("landing.ctaDesc")}</p>
          <Link to="/signup" className="lp-btn lp-btn-white">
            {t("landing.ctaButton")}
            <ArrowRight size={18} />
          </Link>
          <div className="lp-cta-bonus">
            <Gift size={16} />
            {t("landing.ctaBonus")}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

export default LandingPage;
